import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { normalize } from '@origo_vl_banking/utils';

import Container from './components/Container';
import LabelContainer from './components/LabelContainer';
import InputContainer from './components/InputContainer';
import StyledLabel from './components/StyledLabel';
import StyledInput from './components/StyledInput';

const SideText = styled.span`
  padding-left: 8px;
  font-weight: bold;
  font-size: 14px;
  ${({ inInput }) => {
    if (inInput) {
      return css`
        margin-right: 8px;
      `;
    }

    return null;
  }}
`;

const MessageContainer = styled.div`
  ${({ errorSpacing }) => !errorSpacing
    && css`
      position: absolute;
      margin-bottom: 4px;
    `}
  width: calc(100% - 2px);
  background: transparent;
  z-index: 5;
`;

const MessageOuterContainer = styled.div`
  position: relative;
  width: ${({ width }) => width || '100%'};
  display: ${({ visible }) => !visible && 'none'};
`;

const Message = styled.div`
  display: flex;
  padding-top: ${({ errorInLabel }) => (errorInLabel ? '0px' : '5px')};
  padding-left: 5px;
  white-space: normal;
  font-size: 14px;
  color: ${({ theme }) => (theme ? theme.danger : '#e74c3c')};
  justify-content: ${({ justifyErrorMessage }) => justifyErrorMessage || 'flex-start'};
`;

const LabelErrorMessageContainer = styled.div``;

class TextInput extends PureComponent {
  state = {
    value: this.props.value,
  };

  componentDidMount() {
    this.setValueToState();
  }

  componentDidUpdate = (prevProps) => {
    const { value } = this.props;
    if (prevProps.value !== value) {
      this.setValueToState();
    }
  };

  setValueToState = () => {
    const { value } = this.props;
    const newValue = this.normalizeValue(value);
    if (this.state.value !== newValue) {
      this.setState(prevState => ({
        value: prevState.value !== newValue && newValue,
      }));
    }
  };

  normalizeValue = (value) => {
    const isFunction = typeof this.props.normalize === 'function';
    switch (this.props.normalize) {
      case 'amount':
        return normalize.amount(value);
      case 'number':
        return normalize.number(value);
      case 'ssn':
        return normalize.ssn(value);
      case 'creditCard':
        return normalize.creditCard(value);
      default:
        if (isFunction) {
          return this.props.normalize(value);
        }
        return value;
    }
  };

  autotab = (e) => {
    const { tabTo, maxLength } = this.props;

    const { length } = e.target.value;
    if (length === maxLength) {
      const { form } = e.target;
      if (form) {
        form.elements[tabTo].focus();
      }
    }
  };

  handleChange = (e) => {
    const { tabTo, maxLength, onChange } = this.props;
    const normalized = this.normalizeValue(e.target.value);
    this.setState({ value: normalized });
    if (tabTo && maxLength) {
      this.autotab(e);
    }
    if (onChange) {
      onChange(e);
    }
  };

  render() {
    const {
      addWidth,
      align,
      autoComplete,
      autoFocus,
      border,
      disabled,
      error,
      errorSpacing,
      flexDirection,
      fontSize,
      height,
      justifyErrorMessage,
      label,
      labelWidth,
      margin,
      maxLength,
      name,
      onBlur,
      paddingRight,
      placeholder,
      rightSideText,
      showLabel,
      star,
      type,
      width,
      errorInLabel,
      tabIndex,
      onKeyPress,
      labelColor,
      unBoldLabel,
      noMobileWidth,
      // ...rest
    } = this.props;

    const showError = error && error !== 'Form.Error.required-field';
    return (
      <Container
        paddingRight={paddingRight}
        width={width}
        addWidth={addWidth}
        flexDirection={flexDirection}
        noMobileWidth={noMobileWidth}
      >
        {label && showLabel && (
          <LabelContainer errorInLabel={errorInLabel} labelWidth={labelWidth}>
            <StyledLabel
              errorInLabel={errorInLabel && showError}
              margin={margin}
              width={width}
              labelColor={labelColor}
              unBoldLabel={unBoldLabel}
            >
              <FormattedMessage id={label} />
              {star && <span style={{ color: star }}>*</span>}
            </StyledLabel>
            {errorInLabel && showError && (
              <LabelErrorMessageContainer width={width} visible={error}>
                <Message errorInLabel={errorInLabel}>
                  <FormattedMessage id={error} />
                </Message>
              </LabelErrorMessageContainer>
            )}
          </LabelContainer>
        )}
        <InputContainer
          error={error}
          border={border}
          rightSideInInput={rightSideText.inInput && rightSideText.inInput}
          disabled={disabled}
        >
          <StyledInput
            align={align}
            border={border}
            type={type}
            name={name}
            onChange={this.handleChange}
            onBlur={onBlur}
            error={error}
            placeholder={placeholder}
            width={width}
            height={height}
            autoComplete={autoComplete}
            maxLength={maxLength}
            value={this.state.value || ''}
            autoFocus={autoFocus}
            fontSize={fontSize}
            disabled={disabled}
            tabIndex={tabIndex}
            rightSideInInput={rightSideText.inInput && rightSideText.inInput}
            onKeyPress={onKeyPress}
          />
          {rightSideText && rightSideText.show !== false && (
            <SideText {...rightSideText}>{rightSideText.text || rightSideText}</SideText>
          )}
        </InputContainer>
        {!errorInLabel && showError && (
          <MessageOuterContainer width={width} visible={error}>
            <MessageContainer errorSpacing={errorSpacing}>
              <Message justifyErrorMessage={justifyErrorMessage}>
                <FormattedMessage id={error} />
              </Message>
            </MessageContainer>
          </MessageOuterContainer>
        )}
      </Container>
    );
  }
}

TextInput.defaultProps = {
  addWidth: false,
  align: '',
  autoComplete: '',
  autoFocus: false,
  border: null,
  disabled: false,
  error: null,
  errorInLabel: false,
  errorSpacing: false,
  flexDirection: 'column',
  fontSize: '',
  height: '',
  justifyErrorMessage: 'flex-start',
  label: '',
  labelWidth: 'unset',
  margin: true,
  maxLength: 100,
  normalize: '',
  onBlur: () => null,
  onChange: () => null,
  onKeyPress: () => null,
  paddingRight: false,
  placeholder: null,
  rightSideText: '',
  showLabel: true,
  star: '',
  tabIndex: '0',
  tabTo: null,
  type: 'text',
  value: '',
  width: '',
  labelColor: '',
  unBoldLabel: false,
  noMobileWidth: false,
};

TextInput.propTypes = {
  addWidth: PropTypes.bool,
  align: PropTypes.string,
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  border: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Moves the error message up to the label */
  errorInLabel: PropTypes.bool,
  errorSpacing: PropTypes.bool,
  flexDirection: PropTypes.string,
  fontSize: PropTypes.string,
  height: PropTypes.string,
  /** Sets the justifyContent value of the error message */
  justifyErrorMessage: PropTypes.string,
  label: PropTypes.string,
  labelWidth: PropTypes.string,
  margin: PropTypes.bool,
  maxLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  normalize: PropTypes.string,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  onChange: PropTypes.func,
  paddingRight: PropTypes.bool,
  placeholder: PropTypes.string,
  rightSideText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showLabel: PropTypes.bool,
  /** Adds a star next to the input field, indicating that it's required. */
  star: PropTypes.string,
  /** Explicitly sets the tabIndex for the input.
   * Be careful with this, as this can mess up keyboard navigation.
   * */
  tabIndex: PropTypes.string,
  tabTo: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.string,
  labelColor: PropTypes.string,
  unBoldLabel: PropTypes.bool,
  noMobileWidth: PropTypes.bool,
};

export default TextInput;
